import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Select } from "@/components/ui/Select";
import { useReference } from "@/hooks/useReference";
import type { Team } from "@/lib/types";

export interface Filters {
  q: string;
  team: string;
  status: string;
  from: string;
  to: string;
  specimen_type: string;
  organism_code: string;
  has_open_flag: string;
}

export const EMPTY_FILTERS: Filters = {
  q: "",
  team: "",
  status: "",
  from: "",
  to: "",
  specimen_type: "",
  organism_code: "",
  has_open_flag: "",
};

/** The sticky filter bar on the master view (M-01, docs/08 §8.6). */
export function FilterBar({
  filters,
  onChange,
  teams,
  showTeamFilter,
  searchRef,
}: {
  filters: Filters;
  onChange: (filters: Filters) => void;
  teams: Team[];
  showTeamFilter: boolean;
  searchRef?: React.RefObject<HTMLInputElement>;
}) {
  const { organisms, specimens } = useReference();
  const set = <K extends keyof Filters>(key: K, value: Filters[K]) =>
    onChange({ ...filters, [key]: value });

  const active = Object.values(filters).some((value) => value !== "");

  return (
    <div className="sticky top-[6.5rem] z-20 space-y-2 rounded-lg border border-slate-200 bg-white p-3">
      <div className="flex flex-wrap gap-2">
        <Input
          ref={searchRef}
          type="search"
          placeholder="Record ID, lab number or hospital number"
          aria-label="Search isolates"
          className="min-w-[14rem] flex-1"
          value={filters.q}
          onChange={(event) => set("q", event.target.value)}
        />
        {active && (
          <Button variant="ghost" size="md" onClick={() => onChange(EMPTY_FILTERS)}>
            Clear filters
          </Button>
        )}
      </div>

      <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
        {showTeamFilter && (
          <Select
            aria-label="Team"
            placeholder="All teams"
            value={filters.team}
            onChange={(event) => set("team", event.target.value)}
            options={teams.map((team) => ({ value: team.slug, label: team.name }))}
          />
        )}
        <Select
          aria-label="Status"
          placeholder="All statuses"
          value={filters.status}
          onChange={(event) => set("status", event.target.value)}
          options={[
            { value: "complete", label: "Complete" },
            { value: "draft", label: "Draft" },
            { value: "needs_review", label: "Needs review" },
            { value: "illegible", label: "Illegible" },
            { value: "void", label: "Void" },
          ]}
        />
        <Select
          aria-label="Specimen type"
          placeholder="All specimens"
          value={filters.specimen_type}
          onChange={(event) => set("specimen_type", event.target.value)}
          options={specimens.map((s) => ({ value: s.code, label: s.name }))}
        />
        <Select
          aria-label="Organism"
          placeholder="All organisms"
          value={filters.organism_code}
          onChange={(event) => set("organism_code", event.target.value)}
          options={organisms.map((o) => ({ value: o.code, label: o.name }))}
        />
        <Select
          aria-label="QA flags"
          placeholder="Any flag state"
          value={filters.has_open_flag}
          onChange={(event) => set("has_open_flag", event.target.value)}
          options={[
            { value: "true", label: "Has open flags" },
            { value: "false", label: "No open flags" },
          ]}
        />
        <Input
          type="date"
          aria-label="Specimen date from"
          value={filters.from}
          onChange={(event) => set("from", event.target.value)}
        />
        <Input
          type="date"
          aria-label="Specimen date to"
          value={filters.to}
          onChange={(event) => set("to", event.target.value)}
        />
      </div>
    </div>
  );
}
